import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { NavLink } from '@/components/NavLink';
import Logo from './Logo';
import MobileMenu from './MobileMenu';
import { useAuth } from '@/contexts/AuthContext';
import VerifyPhoneDialog from '@/components/edit-profile/VerifyPhoneDialog';

interface HeaderProps {
  subtitle?: string;
  ctaText?: string;
  vendorId?: string; // UUID of current vendor for admin edit link
}

const navItems = [
  { label: 'Domů', to: '/' },
  { label: 'Občerstvení', to: '/katalog' },
  { label: 'Aktivity a řemesla', to: '/aktivity' },
  { label: 'Novinky', to: '/novinky' },
  { label: 'Kontakt', to: '/kontakt' },
];

const Header = ({ subtitle = 'VŠE PRO VÁŠ FESTIVAL', ctaText = 'Přidat záznam', vendorId }: HeaderProps) => {
  const { user, role } = useAuth();
  const location = useLocation();
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const getAdminLink = () => {
    // On vendor detail page - link to edit that vendor (use UUID)
    if (location.pathname.startsWith('/stanek/') && vendorId) {
      return `/admin/stanky/${vendorId}`;
    }
    // On vendor catalog - link to admin vendors list
    if (location.pathname === '/katalog') {
      return '/admin/stanky';
    }
    // On activity detail page - link to edit that activity (use UUID)
    if (location.pathname.startsWith('/aktivita/') && vendorId) {
      return `/admin/aktivity/${vendorId}`;
    }
    // On activities catalog - link to admin activities list
    if (location.pathname === '/aktivity') {
      return '/admin/aktivity';
    }
    // On other pages - link to admin dashboard
    return '/admin';
  };

  const isLoggedIn = user && role;

  return (
    <header className="sticky top-0 z-50 w-full bg-brand-dark/95 backdrop-blur-md border-b border-white/10 rounded-b-2xl">
      <div className="container mx-auto px-6 h-20 flex items-center justify-between">
        <Logo subtitle={subtitle} variant="dark" />

        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              className="text-sm font-semibold text-white/70 hover:text-primary transition-colors"
              activeClassName="text-primary"
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {isLoggedIn && (
            <Link
              to={getAdminLink()}
              className="hidden sm:flex items-center gap-2 text-sm font-semibold text-white/50 hover:text-primary transition-colors"
            >
              <span className="material-symbols-outlined text-lg">admin_panel_settings</span>
              <span className="hidden md:inline">Admin</span>
            </Link>
          )}
          <button
            type="button"
            onClick={() => setEditDialogOpen(true)}
            className="hidden sm:flex items-center gap-2 text-sm font-semibold text-white/50 hover:text-primary transition-colors"
          >
            <span className="material-symbols-outlined text-lg">edit</span>
            <span className="hidden md:inline">Upravit profil</span>
          </button>
          <a
            href="/pridat"
            className="hidden sm:flex items-center gap-2 bg-primary text-brand-dark px-6 py-2.5 rounded-full font-bold text-xs uppercase tracking-tight hover:shadow-lg hover:shadow-primary/20 transition-all"
          >
            <span className="material-symbols-outlined text-base">add_circle</span>
            {ctaText}
          </a>
          <MobileMenu
            ctaText={ctaText}
            isLoggedIn={!!isLoggedIn}
            adminLink={getAdminLink()}
            onEditProfileClick={() => setEditDialogOpen(true)}
          />
        </div>
      </div>
      <VerifyPhoneDialog open={editDialogOpen} onOpenChange={setEditDialogOpen} />
    </header>
  );
};

export default Header;
